import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Backdrop,
    CircularProgress,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axiosInstance from "../../../api/axiosInstance";
import useNotification from "../../common/Notification";

const ACCEPTED_TYPES = ".xlsx,.xls,.csv";
const MAX_SIZE_MB = 10;

const UploadExcel = ({ onUploadSuccess }) => {
    const [file, setFile] = useState(null);
    const [templateId, setTemplateId] = useState("");
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const showNotification = useNotification();

    const handleFileChange = (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;
        const sizeMB = selected.size / (1024 * 1024);
        if (sizeMB > MAX_SIZE_MB) {
            showNotification(`File size must be under ${MAX_SIZE_MB}MB`, "error");
            return;
        }
        const ext = selected.name.split(".").pop()?.toLowerCase();
        if (!["xlsx", "xls", "csv"].includes(ext)) {
            showNotification("Only .xlsx, .xls, or .csv files are allowed", "error");
            return;
        }
        setFile(selected);
        setResult(null);
        setWarnings([]);
    };

    const handleSubmit = async () => {
        if (!file) {
            showNotification("Please select a file", "warning");
            return;
        }
        setUploading(true);
        setResult(null);
        setWarnings([]);
        try {
            const formData = new FormData();
            formData.append("file", file);
            if (templateId.trim() !== "") {
                const tid = parseInt(templateId, 10);
                if (!Number.isNaN(tid)) formData.append("template_id", tid);
            }
            const response = await axiosInstance.post(
                "/whatsapp-business/automation/upload-excel",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            if (response.data?.success) {
                setResult(response.data.data || response.data);
                setWarnings(response.data.warnings || []);
                showNotification(
                    response.data.message || "Excel uploaded and processed successfully",
                    "success"
                );
                if (typeof onUploadSuccess === "function") onUploadSuccess();
            } else {
                showNotification(response.data?.message || "Upload failed", "error");
            }
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.errors?.file?.[0] ||
                err.message ||
                "Upload failed";
            showNotification(msg, "error");
        } finally {
            setUploading(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setTemplateId("");
        setResult(null);
        setWarnings([]);
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Info block */}
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                    borderColor: "divider",
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                }}
            >
                <InfoOutlinedIcon sx={{ color: "info.main", mt: 0.25, fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                    Upload an Excel file with <strong>phrase</strong> and <strong>reply</strong> columns to train the bot. Supported: .xlsx, .xls, .csv (max {MAX_SIZE_MB}MB). You can optionally link all rows to a template ID.
                </Typography>
            </Paper>

            {/* File drop / choose area */}
            <Paper
                component="label"
                variant="outlined"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 4,
                    px: 2,
                    mb: 3,
                    borderRadius: 2,
                    border: "2px dashed",
                    borderColor: file ? "success.main" : "divider",
                    bgcolor: (theme) =>
                        file ? alpha(theme.palette.success.main, 0.08) : "action.hover",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    },
                }}
            >
                <input
                    type="file"
                    hidden
                    accept={ACCEPTED_TYPES}
                    onChange={handleFileChange}
                />
                {file ? (
                    <>
                        <InsertDriveFileIcon sx={{ fontSize: 48, color: "success.main", mb: 1 }} />
                        <Typography variant="subtitle1" fontWeight={600} color="success.dark">
                            {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            {(file.size / 1024).toFixed(1)} KB · Click to change file
                        </Typography>
                    </>
                ) : (
                    <>
                        <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                        <Typography variant="subtitle1" color="text.secondary">
                            Choose file
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            .xlsx, .xls or .csv, max {MAX_SIZE_MB}MB
                        </Typography>
                    </>
                )}
            </Paper>

            {/* Template ID */}
            {/* <TextField
                label="Template ID (optional)"
                type="number"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                placeholder="e.g. 123"
                size="small"
                fullWidth
                sx={{ mb: 3 }}
                InputProps={{
                    inputProps: { min: 0 },
                    sx: { borderRadius: 2 },
                }}
            /> */}

            {/* Actions */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!file || uploading}
                    startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                    sx={{ borderRadius: 2, px: 2.5 }}
                >
                    {uploading ? "Uploading…" : "Upload"}
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={uploading}
                    sx={{ borderRadius: 2, px: 2.5 }}
                >
                    Reset
                </Button>
            </Box>

            {/* Success result */}
            {result && (
                <Paper
                    variant="outlined"
                    sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 2,
                        borderColor: "success.main",
                        bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <CheckCircleOutlineIcon color="success" />
                        <Typography variant="subtitle2" fontWeight={600} color="success.dark">
                            Upload complete
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Chip
                            label={`Inserted: ${result.inserted_count ?? 0}`}
                            size="small"
                            color="success"
                            variant="outlined"
                        />
                        <Chip
                            label={`Skipped: ${result.skipped_count ?? 0}`}
                            size="small"
                            variant="outlined"
                        />
                        {result.file_name && (
                            <Chip label={result.file_name} size="small" variant="outlined" />
                        )}
                    </Box>
                </Paper>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
                <Paper
                    variant="outlined"
                    sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 2,
                        borderColor: "warning.main",
                        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <WarningAmberIcon sx={{ color: "warning.dark" }} />
                        <Typography variant="subtitle2" fontWeight={600} color="warning.dark">
                            Warnings
                        </Typography>
                    </Box>
                    <List dense disablePadding>
                        {warnings.map((w, i) => (
                            <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                                <ListItemIcon sx={{ minWidth: 28 }}>
                                    <WarningAmberIcon sx={{ fontSize: 16, color: "warning.dark" }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={typeof w === "string" ? w : w.message || JSON.stringify(w)}
                                    primaryTypographyProps={{ variant: "body2" }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            <Backdrop open={uploading} sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default UploadExcel;
